import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Website } from '../../types';
import { websiteService } from '../../services/websiteService';

interface WebsiteState {
  websites: Website[];
  selectedWebsite: Website | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: WebsiteState = {
  websites: [],
  selectedWebsite: null,
  isLoading: false,
  error: null,
};

export const fetchWebsites = createAsyncThunk(
  'websites/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { websites, error } = await websiteService.getWebsites();
      if (error) {
        return rejectWithValue(error);
      }
      
      return websites;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch websites');
    }
  }
);

export const fetchWebsite = createAsyncThunk(
  'websites/fetchOne',
  async (websiteId: string, { rejectWithValue }) => {
    try {
      const { website, error } = await websiteService.getWebsiteById(parseInt(websiteId));
      if (error) {
        return rejectWithValue(error);
      }
      return website;
    } catch (error: any) {
      return rejectWithValue(error.message || `Failed to fetch website with ID ${websiteId}`);
    }
  }
);

export const createWebsite = createAsyncThunk(
  'websites/create',
  async (
    websiteData: { website_id: string; base_url: string; country: string },
    { rejectWithValue }
  ) => {
    try {
      const { website, error } = await websiteService.createWebsite(websiteData);
      if (error) {
        return rejectWithValue(error);
      }
      return website;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create website');
    }
  }
);

export const updateWebsite = createAsyncThunk(
  'websites/update',
  async (
    { websiteId, data }: { websiteId: string; data: { base_url?: string; country?: string } },
    { rejectWithValue }
  ) => {
    try {
      const { website, error } = await websiteService.updateWebsite(parseInt(websiteId), data);
      if (error) {
        return rejectWithValue(error);
      }
      return website;
    } catch (error: any) {
      return rejectWithValue(error.message || `Failed to update website with ID ${websiteId}`);
    }
  }
);

export const deleteWebsite = createAsyncThunk(
  'websites/delete',
  async (websiteId: string, { rejectWithValue }) => {
    try {
      const { error } = await websiteService.deleteWebsite(parseInt(websiteId));
      if (error) {
        return rejectWithValue(error);
      }
      return websiteId;
    } catch (error: any) {
      return rejectWithValue(error.message || `Failed to delete website with ID ${websiteId}`);
    }
  }
);

const websiteSlice = createSlice({
  name: 'website',
  initialState,
  reducers: {
    setSelectedWebsite: (state, action: PayloadAction<Website | null>) => {
      state.selectedWebsite = action.payload;
    },
    clearWebsiteError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all websites
      .addCase(fetchWebsites.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWebsites.fulfilled, (state, action) => {
        state.isLoading = false;
        state.websites = action.payload;
        
        // If no website is selected, select the first one
        if (!state.selectedWebsite && action.payload.length > 0) {
          state.selectedWebsite = action.payload[0];
        }
      })
      .addCase(fetchWebsites.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Fetch one website
      .addCase(fetchWebsite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWebsite.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedWebsite = action.payload;
        
        // Update the website in the list if it exists
        const index = state.websites.findIndex((w) => w.website_id === action.payload?.website_id);
        if (index !== -1 && action.payload) {
          state.websites[index] = action.payload;
        }
      })
      .addCase(fetchWebsite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create website
      .addCase(createWebsite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createWebsite.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          state.websites.push(action.payload);
          state.selectedWebsite = action.payload;
        }
      })
      .addCase(createWebsite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Update website
      .addCase(updateWebsite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWebsite.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          const index = state.websites.findIndex((w) => w.website_id === action.payload?.website_id);
          if (index !== -1) {
            state.websites[index] = action.payload;
          }
          if (state.selectedWebsite?.website_id === action.payload.website_id) {
            state.selectedWebsite = action.payload;
          }
        }
      })
      .addCase(updateWebsite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Delete website
      .addCase(deleteWebsite.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWebsite.fulfilled, (state, action) => {
        state.isLoading = false;
        state.websites = state.websites.filter((w) => w.website_id !== action.payload);
        if (state.selectedWebsite?.website_id === action.payload) {
          state.selectedWebsite = state.websites.length > 0 ? state.websites[0] : null;
        }
      })
      .addCase(deleteWebsite.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setSelectedWebsite, clearWebsiteError } = websiteSlice.actions;
export default websiteSlice.reducer;